import * as THREE from 'three'
const logger = console
import { disposeGlbResult, disposeMaterialTextures } from '../loaders/cache'
import { disposeCachedPartResult } from '../loaders/part'
import { disposeImportedModelTopologyHosts } from '../topology/imported-model-topology-runtime'
import { syncImportedModelBodyRepresentations } from '../model-body/syncImportedModelBodyRepresentations'
import { addObject3DToParent } from '../../utils/scene-object/addObject3DToParent'
import {
  notifySectionableModelRootContentChanged,
  resolveSectionableModelRoot,
} from '../../section-view'
import type {
  CachedPartResult,
  ComposedAssemblyResult,
  ImportedModelResult,
} from '../loaders/types'

type SceneMountableGlbResult = {
  partName: string
  object: THREE.Group
}

/**
 * 收集当前场景中由 imported-model 挂载入口创建的顶层装配根。
 *
 * 设计说明：
 * 1. 历史版本切换、项目模型重组和离线包导入都复用 `imported-assembly` 作为可见文档根；
 * 2. 若旧流程曾经重复挂载多个同名根，`getObjectByName` 只会命中第一个，导致旧模型残留；
 * 3. 新文档扫描可剖切模型根的直接子节点，旧文档仍兼容 scene 直接子节点；
 * 4. 不递归扫描装配内部，避免误删同名业务子树。
 */
function collectMountedAssemblyRoots(scene: THREE.Scene): THREE.Object3D[] {
  const sectionableModelRoot: THREE.Object3D | null = resolveSectionableModelRoot(scene)
  const mountScopes = sectionableModelRoot && sectionableModelRoot !== scene
    ? [sectionableModelRoot, scene]
    : [scene]
  return mountScopes.flatMap((scope) => (
    scope.children.filter((child) => child.name === 'imported-assembly')
  ))
}

/**
 * 将导入模型对象挂载到统一的 imported assembly 场景外壳。
 *
 * 设计说明：
 * 1. legacy GLB、cached part 与 composed assembly 必须使用同一类顶层挂载外壳；
 * 2. 切换/回滚/移除只认这一个生命周期边界，避免 composed root 直接挂到 scene 后无法被旧清理入口摘除；
 * 3. 外壳自身不改变子模型局部矩阵，只负责表达“当前导入文档根”这个运行时身份。
 */
function mountObjectInImportedAssemblyGroup(object: THREE.Object3D, scene: THREE.Scene): void {
  detachAssemblyFromScene(scene)
  const assemblyGroup = new THREE.Group()
  assemblyGroup.name = 'imported-assembly'
  addObject3DToParent(object, assemblyGroup)
  addObject3DToParent(assemblyGroup, resolveSectionableModelRoot(scene) ?? scene)
  syncImportedModelBodyRepresentations(scene)
  notifySectionableModelRootContentChanged(scene)
}

export function addGlbResultToScene(result: SceneMountableGlbResult, scene: THREE.Scene): void {
  mountObjectInImportedAssemblyGroup(result.object, scene)
  logger.info(`[imported-model-scene] 已切换场景到: ${result.partName}`)
}

export function detachAssemblyFromScene(scene: THREE.Scene): void {
  const assemblies = collectMountedAssemblyRoots(scene)
  if (assemblies.length === 0) {
    return
  }
  assemblies.forEach((assembly) => assembly.removeFromParent())
  notifySectionableModelRootContentChanged(scene)
  syncImportedModelBodyRepresentations(scene)
  logger.info('[imported-model-scene] 已从场景摘除装配体（资源保留）')
}

export function removeAssemblyFromScene(scene: THREE.Scene): void {
  const assemblies = collectMountedAssemblyRoots(scene)
  if (assemblies.length === 0) {
    return
  }
  assemblies.forEach((assembly) => assembly.removeFromParent())
  // 先让剖面盖解除共享 geometry 引用，再释放导入模型资源。
  notifySectionableModelRootContentChanged(scene)
  assemblies.forEach((assembly) => disposeImportedModelObjectTreeResources(assembly))
  syncImportedModelBodyRepresentations(scene)
  logger.info('[imported-model-scene] 已移除装配体')
}

/**
 * 释放场景子树内所有 mesh 的几何体、材质和材质贴图。
 *
 * 设计说明：
 * `removeAssemblyFromScene` 与完整 composed assembly 释放都需要同一套 GPU 资源释放路径。
 * 贴图必须先于 material.dispose() 释放，否则重复导入 `.puppy` 或 GLB 时会遗留纹理资源。
 */
export function disposeImportedModelObjectTreeResources(root: THREE.Object3D): void {
  disposeImportedModelTopologyHosts(root)
  const disposedGeometries = new Set<THREE.BufferGeometry>()
  const disposedMaterials = new Set<THREE.Material>()
  const disposedTextures = new Set<THREE.Texture>()
  root.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) {
      return
    }
    if (child.geometry && !disposedGeometries.has(child.geometry)) {
      disposedGeometries.add(child.geometry)
      child.geometry.dispose()
    }
    const materials = Array.isArray(child.material) ? child.material : [child.material]
    materials.forEach((material) => {
      if (!material) {
        return
      }
      if (disposedMaterials.has(material)) {
        return
      }
      disposedMaterials.add(material)
      disposeMaterialTextures(material, undefined, disposedTextures)
      material.dispose()
    })
  })
}

function disposeComposedAssemblyResult(result: ComposedAssemblyResult): void {
  // 实例投影必须先恢复 source Mesh 并释放额外 scope，再销毁拓扑宿主和底层共享资源。
  result.instancingRuntime?.dispose()
  delete result.instancingRuntime
  if (result.resourceScope) {
    // topology host 仍引用场景对象与 BRep 运行时，必须先于底层 GPU lease 释放。
    disposeImportedModelTopologyHosts(result.root)
    result.resourceScope.release()
  } else {
    disposeImportedModelObjectTreeResources(result.root)
  }
  result.root.removeFromParent()
  result.root.clear()
}

function isSceneGraphResult(
  result: ImportedModelResult,
): result is CachedPartResult | Extract<ImportedModelResult, { kind: 'legacy-glb' }> {
  return result.kind === 'legacy-glb' || result.kind === 'cached-part'
}

export function mountImportedModelToScene(result: ImportedModelResult, scene: THREE.Scene): void {
  if (isSceneGraphResult(result)) {
    addGlbResultToScene(result, scene)
    return
  }
  mountObjectInImportedAssemblyGroup(result.root, scene)
}

export function detachImportedAssemblyFromScene(scene: THREE.Scene): void {
  detachAssemblyFromScene(scene)
}

export function removeImportedAssemblyFromScene(scene: THREE.Scene): void {
  removeAssemblyFromScene(scene)
}

export function disposeImportedModelInstance(result: ImportedModelResult): void {
  if (result.kind === 'legacy-glb') {
    disposeImportedModelTopologyHosts(result.object)
    result.object.removeFromParent()
    result.object.clear()
    return
  }
  if (result.kind === 'cached-part') {
    disposeImportedModelTopologyHosts(result.object)
    result.object.removeFromParent()
    result.object.clear()
    return
  }
  // 缓存只释放场景实例关系时，派生 InstancedMesh 仍必须先退出 Viewer 运行时。
  result.instancingRuntime?.dispose()
  delete result.instancingRuntime
  disposeImportedModelTopologyHosts(result.root)
  result.root.removeFromParent()
  result.root.clear()
}

export function disposeImportedModel(result: ImportedModelResult): void {
  if (result.kind === 'legacy-glb') {
    disposeImportedModelTopologyHosts(result.object)
    disposeGlbResult(result)
    return
  }
  if (result.kind === 'cached-part') {
    disposeImportedModelTopologyHosts(result.object)
    disposeCachedPartResult(result)
    return
  }
  disposeComposedAssemblyResult(result)
}

/**
 * 释放已经提交到运行时或语义缓存的导入结果。
 *
 * complete composed result 的显式 scope 是真实资源 owner，必须完整释放并把异常传播给
 * 快照会话；cached-part 与无独立 scope 的 composed result 由语义缓存持有 GPU 资源，
 * 此处只销毁实例关系；legacy 结果没有其它 owner，仍执行完整释放。
 *
 * @param result 已完成缓存事务提交的导入结果。
 */
export function disposeCommittedImportedModelResult(result: ImportedModelResult): void {
  if (result.kind === 'composed-assembly' && result.resourceScope) {
    disposeImportedModel(result)
    return
  }
  if (result.kind !== 'legacy-glb') {
    disposeImportedModelInstance(result)
    return
  }
  disposeImportedModel(result)
}
